// src/utils/customerModel.js

// 👥 Customer Schema
export const CustomerSchema = {
  _id: '',
  name: '',
  mobileNumber: '',
  branch: '', // Reference to branch ID
  dateOfBirth: '',
  pointsBalance: 0,
  lifetimePointsEarned: 0,
  lifetimePointsRedeemed: 0,
  loyaltyPoints: 0, // Keep for backward compatibility
  totalOrders: 0,
  totalSpend: 0,
  lastOrderDate: null,
  isActive: true,
  createdBy: null,
  createdAt: null,
  updatedAt: null
}

// 🛡️ Validation Rules
export const VALIDATION_RULES = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z\s\-'.]+$/,
    message: 'Name can only contain letters, spaces, hyphens, apostrophes, and periods'
  },
  mobileNumber: {
    required: true,
    minLength: 10,
    maxLength: 15,
    pattern: /^[0-9]+$/,
    message: 'Mobile number must contain only digits and be 10-15 characters long'
  },
  branch: {
    required: true,
    message: 'Branch is required'
  },
  dateOfBirth: {
    required: false,
    message: 'Date of birth is required',
    maxAge: 120,
    minAge: 0
  },
  pointsBalance: {
    required: false,
    min: 0,
    max: 999999,
    message: 'Points balance must be a positive number'
  },
  lifetimePointsEarned: {
    required: false,
    min: 0,
    max: 999999,
    message: 'Lifetime points earned must be a positive number'
  },
  lifetimePointsRedeemed: {
    required: false,
    min: 0,
    max: 999999,
    message: 'Lifetime points redeemed must be a positive number'
  },
  loyaltyPoints: {
    required: false,
    min: 0,
    max: 999999,
    message: 'Loyalty points must be a positive number'
  }
}

// 👥 Mobile Number Conversion Utilities
export const MobileNumberUtils = {
  // Convert 0714005511 to 94714005511 for backend
  formatMobileForBackend: (mobileNumber) => {
    if (!mobileNumber) return ''
    const cleanNumber = mobileNumber.toString().trim()
    
    // If starts with 0 and is 10 digits, replace first 0 with 94
    if (cleanNumber.startsWith('0') && cleanNumber.length === 10) {
      return '94' + cleanNumber.substring(1)
    }
    
    return cleanNumber
  },
  
  // Convert 94714005511 back to 0714005511 for frontend display
  formatMobileForDisplay: (mobileNumber) => {
    if (!mobileNumber) return ''
    const cleanNumber = mobileNumber.toString().trim()
    
    // If starts with 94 and is 11 digits, replace 94 with 0
    if (cleanNumber.startsWith('94') && cleanNumber.length === 11) {
      return '0' + cleanNumber.substring(2)
    }
    
    return cleanNumber
  }
}

// 👥 Customer Model Class
export class CustomerModel {
  
  // Format mobile number for API submission (converts 0714005511 to 94714005511)
  static formatMobileForAPI(mobileNumber) {
    return MobileNumberUtils.formatMobileForBackend(mobileNumber)
  }
  
  // Convert mobile number for form input display (converts 94714005511 to 0714005511)
  static convertMobileForFormInput(mobileNumber) {
    return MobileNumberUtils.formatMobileForDisplay(mobileNumber)
  }
  
  // Create a new customer instance
  static create(data = {}) {
    return {
      ...CustomerSchema,
      ...data,
      _id: data._id || `temp_${Date.now()}`,
      pointsBalance: data.pointsBalance || 0,
      lifetimePointsEarned: data.lifetimePointsEarned || 0,
      lifetimePointsRedeemed: data.lifetimePointsRedeemed || 0,
      loyaltyPoints: data.loyaltyPoints || data.pointsBalance || 0, // Backward compatibility
      totalOrders: data.totalOrders || 0,
      totalSpend: data.totalSpend || 0,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString()
    }
  }
  
  // Validate customer data
  static validate(customerData) {
    const errors = {}
    
    // Validate name
    if (!customerData.name || !customerData.name.trim()) {
      errors.name = 'Customer name is required'
    } else if (customerData.name.length < VALIDATION_RULES.name.minLength) {
      errors.name = `Name must be at least ${VALIDATION_RULES.name.minLength} characters`
    } else if (customerData.name.length > VALIDATION_RULES.name.maxLength) {
      errors.name = `Name must be less than ${VALIDATION_RULES.name.maxLength} characters`
    } else if (!VALIDATION_RULES.name.pattern.test(customerData.name)) {
      errors.name = VALIDATION_RULES.name.message
    }
    
    // Validate mobile number
    if (!customerData.mobileNumber || !customerData.mobileNumber.trim()) {
      errors.mobileNumber = 'Mobile number is required'
    } else if (customerData.mobileNumber.length < VALIDATION_RULES.mobileNumber.minLength) {
      errors.mobileNumber = `Mobile number must be at least ${VALIDATION_RULES.mobileNumber.minLength} digits`
    } else if (customerData.mobileNumber.length > VALIDATION_RULES.mobileNumber.maxLength) {
      errors.mobileNumber = `Mobile number must be less than ${VALIDATION_RULES.mobileNumber.maxLength} digits`
    } else if (!VALIDATION_RULES.mobileNumber.pattern.test(customerData.mobileNumber)) {
      errors.mobileNumber = VALIDATION_RULES.mobileNumber.message
    }
    
    // Validate branch
    if (!customerData.branch || !customerData.branch.trim()) {
      errors.branch = VALIDATION_RULES.branch.message
    }
    
    // Validate date of birth (optional now)
    if (customerData.dateOfBirth && customerData.dateOfBirth.trim()) {
      const birthDate = new Date(customerData.dateOfBirth)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }
      
      if (age < VALIDATION_RULES.dateOfBirth.minAge || age > VALIDATION_RULES.dateOfBirth.maxAge) {
        errors.dateOfBirth = `Age must be between ${VALIDATION_RULES.dateOfBirth.minAge} and ${VALIDATION_RULES.dateOfBirth.maxAge} years`
      }
      
      if (birthDate > today) {
        errors.dateOfBirth = 'Date of birth cannot be in the future'
      }
    }
    
    // Validate loyalty points (backward compatibility)
    if (customerData.loyaltyPoints !== undefined && customerData.loyaltyPoints !== null && customerData.loyaltyPoints !== '') {
      const points = Number(customerData.loyaltyPoints)
      if (isNaN(points)) {
        errors.loyaltyPoints = 'Loyalty points must be a valid number'
      } else if (points < VALIDATION_RULES.loyaltyPoints.min) {
        errors.loyaltyPoints = `Loyalty points must be at least ${VALIDATION_RULES.loyaltyPoints.min}`
      } else if (points > VALIDATION_RULES.loyaltyPoints.max) {
        errors.loyaltyPoints = VALIDATION_RULES.loyaltyPoints.message
      }
    }
    
    // Validate points balance
    if (customerData.pointsBalance !== undefined && customerData.pointsBalance !== null && customerData.pointsBalance !== '') {
      const points = Number(customerData.pointsBalance)
      if (isNaN(points)) {
        errors.pointsBalance = 'Points balance must be a valid number'
      } else if (points < VALIDATION_RULES.pointsBalance.min) {
        errors.pointsBalance = `Points balance must be at least ${VALIDATION_RULES.pointsBalance.min}`
      } else if (points > VALIDATION_RULES.pointsBalance.max) {
        errors.pointsBalance = VALIDATION_RULES.pointsBalance.message
      }
    }
    
    // Validate lifetime points earned
    if (customerData.lifetimePointsEarned !== undefined && customerData.lifetimePointsEarned !== null && customerData.lifetimePointsEarned !== '') {
      const points = Number(customerData.lifetimePointsEarned)
      if (isNaN(points)) {
        errors.lifetimePointsEarned = 'Lifetime points earned must be a valid number'
      } else if (points < VALIDATION_RULES.lifetimePointsEarned.min) {
        errors.lifetimePointsEarned = `Lifetime points earned must be at least ${VALIDATION_RULES.lifetimePointsEarned.min}`
      } else if (points > VALIDATION_RULES.lifetimePointsEarned.max) {
        errors.lifetimePointsEarned = VALIDATION_RULES.lifetimePointsEarned.message
      }
    }
    
    // Validate lifetime points redeemed
    if (customerData.lifetimePointsRedeemed !== undefined && customerData.lifetimePointsRedeemed !== null && customerData.lifetimePointsRedeemed !== '') {
      const points = Number(customerData.lifetimePointsRedeemed)
      if (isNaN(points)) {
        errors.lifetimePointsRedeemed = 'Lifetime points redeemed must be a valid number'
      } else if (points < VALIDATION_RULES.lifetimePointsRedeemed.min) {
        errors.lifetimePointsRedeemed = `Lifetime points redeemed must be at least ${VALIDATION_RULES.lifetimePointsRedeemed.min}`
      } else if (points > VALIDATION_RULES.lifetimePointsRedeemed.max) {
        errors.lifetimePointsRedeemed = VALIDATION_RULES.lifetimePointsRedeemed.message
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Validate partial customer data (for updates)
  static validatePartial(customerData) {
    const errors = {}
    
    // Only validate fields that are present
    if (customerData.name !== undefined) {
      if (!customerData.name || !customerData.name.trim()) {
        errors.name = 'Customer name is required'
      } else if (customerData.name.length < VALIDATION_RULES.name.minLength) {
        errors.name = `Name must be at least ${VALIDATION_RULES.name.minLength} characters`
      } else if (customerData.name.length > VALIDATION_RULES.name.maxLength) {
        errors.name = `Name must be less than ${VALIDATION_RULES.name.maxLength} characters`
      } else if (!VALIDATION_RULES.name.pattern.test(customerData.name)) {
        errors.name = VALIDATION_RULES.name.message
      }
    }
    
    if (customerData.mobileNumber !== undefined) {
      if (!customerData.mobileNumber || !customerData.mobileNumber.trim()) {
        errors.mobileNumber = 'Mobile number is required'
      } else if (!VALIDATION_RULES.mobileNumber.pattern.test(customerData.mobileNumber)) {
        errors.mobileNumber = VALIDATION_RULES.mobileNumber.message
      }
    }
    
    if (customerData.dateOfBirth !== undefined) {
      if (!customerData.dateOfBirth) {
        errors.dateOfBirth = VALIDATION_RULES.dateOfBirth.message
      } else {
        const birthDate = new Date(customerData.dateOfBirth)
        const today = new Date()
        if (birthDate > today) {
          errors.dateOfBirth = 'Date of birth cannot be in the future'
        }
      }
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  // Format customer display name
  static formatName(customer) {
    if (!customer || !customer.name) return 'Unknown Customer'
    return customer.name
  }
  
  // Format mobile number for display (with spacing)
  static formatMobileNumber(mobileNumber) {
    if (!mobileNumber) return 'No mobile number'
    
    // Convert backend format to display format first if needed
    const displayNumber = MobileNumberUtils.formatMobileForDisplay(mobileNumber)
    
    // Format Sri Lankan mobile numbers (e.g., 0778289336 -> 077 828 9336)
    if (displayNumber.length === 10 && displayNumber.startsWith('0')) {
      return `${displayNumber.slice(0, 3)} ${displayNumber.slice(3, 6)} ${displayNumber.slice(6)}`
    }
    
    return displayNumber
  }
  
  // Format loyalty points for display
  static formatLoyaltyPoints(points) {
    if (!points || points === 0) return '0 Points'
    return `${points.toLocaleString()} Points`
  }
  
  // Format points balance for display
  static formatPointsBalance(points) {
    if (!points || points === 0) return '0 Points'
    return `${points.toLocaleString()} Points`
  }
  
  // Format lifetime points earned for display
  static formatLifetimePointsEarned(points) {
    if (!points || points === 0) return '0 Points'
    return `${points.toLocaleString()} Points`
  }
  
  // Format lifetime points redeemed for display
  static formatLifetimePointsRedeemed(points) {
    if (!points || points === 0) return '0 Points'
    return `${points.toLocaleString()} Points`
  }
  
  // Calculate age from date of birth
  static calculateAge(dateOfBirth) {
    if (!dateOfBirth) return null
    
    const birthDate = new Date(dateOfBirth)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    
    return age
  }
  
  // Get customer status badge classes
  static getStatusBadgeClasses(isActive) {
    return isActive
      ? 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'
      : 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800'
  }
  
  // Get loyalty tier based on points (uses pointsBalance or fallback to loyaltyPoints for backward compatibility)
  static getLoyaltyTier(pointsBalance, loyaltyPoints = 0) {
    const points = pointsBalance !== undefined ? pointsBalance : loyaltyPoints
    if (!points || points === 0) return { name: 'Bronze', color: 'bg-orange-100 text-orange-800' }
    if (points < 500) return { name: 'Bronze', color: 'bg-orange-100 text-orange-800' }
    if (points < 1000) return { name: 'Silver', color: 'bg-gray-100 text-gray-800' }
    if (points < 2000) return { name: 'Gold', color: 'bg-yellow-100 text-yellow-800' }
    return { name: 'Platinum', color: 'bg-purple-100 text-purple-800' }
  }
  
  // Create default customer object
  static createDefault() {
    return {
      ...CustomerSchema,
      _id: `temp_${Date.now()}`,
      pointsBalance: 0,
      lifetimePointsEarned: 0,
      lifetimePointsRedeemed: 0,
      loyaltyPoints: 0,
      dateOfBirth: new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  }
  
  // Format total spend for display
  static formatTotalSpend(amount) {
    if (!amount || amount === 0) return 'Rs.0.00'
    return `Rs.${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  
  // Format date for display
  static formatDate(dateString) {
    if (!dateString) return 'Not available'
    
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    } catch {
      return 'Invalid date'
    }
  }
}